import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getAllSpaces, getSpacesIsLoading, fetchAllSpaces } from 'spaces/index';
import SpaceFolderTree from 'documents/containers/SpaceFolderTree/index';
import { getAllRoles, getActiveSpaceRoles } from 'auth/store/roles/selectors';
import { getActiveStateAuditPendingRoleIds } from 'auth/store/audit/selectors';
import { fetchAllRoles } from 'auth/store/roles/actions';
import { getAuthUser, getAuthUserAuthorities, getIsAdmin } from 'auth/store/auth/selectors';
import { fetchMyUser, loginSuccess } from 'auth/store/auth/actions';
import MenuSkeleton from '../Menu/MenuSkeleton';
import theme from '../../../theme';
import {
  DashboardIcon,
  ImportContactsIcon,
  SettingsIcon,
  AppsIcon,
  PersonIcon,
  LockIcon,
  LabelIcon,
  PeopleIcon,
  FileIcon,
  VerifiedUserIcon,
  AlertCircleIcon,
  DeleteIcon,
  WifiOffIcon,
  EmailIcon,
} from '../../components/Icon/index';
import Menu from '../Menu/index';

const isAdminRole = role =>
  role.id === parseInt(process.env.REACT_APP_ROLE_SUPERADMIN, 10) ||
  role.id === parseInt(process.env.REACT_APP_ROLE_ADMIN, 10);

class AppSidebar extends React.Component {
  componentDidMount() {
    this.props.fetchAll();
    this.props.fetchMyUser();
    if (this.props.isAdmin) {
      this.props.fetchAllRoles();
    }

    const token = localStorage.getItem('accessToken');
    if (token) {
      this.props.loginSuccess(token);
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.authUser &&
      (!prevProps.authUser || prevProps.authUser.id !== this.props.authUser.id) &&
      this.props.isAdmin
    ) {
      this.props.fetchAllRoles();
    }
  }

  render() {
    const { spaces, spacesIsLoading, roles, isAdmin, authUser, activeSpaceRoles, roleIdsPendingAudit, authorities } =
      this.props;

    const spacesMenu = spaces.map(space => ({
      name: space.name,
      icon: <AppsIcon />,
      key: `spaces-${space.id}`,
      route: `/spaces/${space.id}/documents`,
      children: [
        {
          name: 'Documents',
          icon: <FileIcon />,
          key: `spaces-${space.id}-docs`,
          route: `/spaces/${space.id}/documents`,
          useExactRoute: true,
          childComponent: <SpaceFolderTree />,
        },
        {
          name: 'Space members',
          icon: <PeopleIcon />,
          key: `spaces-${space.id}-members`,
          useExactRoute: true,
          route: `/spaces/${space.id}/members`,
          iconAfter: space.needsUserAudit ? <AlertCircleIcon color={theme.colors.white} /> : undefined,
          isHidden: !authUser || authUser.denyContactList,
        },
        {
          name: 'Space permissions',
          icon: <LockIcon />,
          key: `spaces-${space.id}-permissions`,
          useExactRoute: true,
          route: `/spaces/${space.id}/permissions`,
          iconAfter: space.needsRoleAudit ? <AlertCircleIcon color={theme.colors.white} /> : undefined,
          isHidden: !isAdmin && (!authUser || !space || !space.owner || authUser.id !== space.owner),
          hasFilter: true,
          filterLabel: 'Filter by role...',
          children:
            activeSpaceRoles.length > 1
              ? activeSpaceRoles
                  .filter(role => !isAdminRole(role))
                  .map(role => ({
                    name: role.name,
                    key: `spaces-${space.id}-permissions-role-${role.id}`,
                    route: `/spaces/${space.id}/permissions/${role.id}`,
                    iconAfter:
                      space && space.needsRoleAudit && roleIdsPendingAudit.includes(role.id) ? (
                        <AlertCircleIcon color={theme.colors.white} />
                      ) : undefined,
                  }))
              : [],
        },
        {
          name: 'Trash',
          icon: <DeleteIcon />,
          key: `spaces-${space.id}-trash`,
          route: `/spaces/${space.id}/trash`,
          useExactRoute: true,
          isHidden: !isAdmin,
        },
      ],
    }));

    const spacesMenuFilterProps =
      spaces && spaces.length > 5
        ? {
            hasFilter: true,
            filterLabel: 'Filter by name...',
          }
        : {};

    const items = [
      {
        name: 'Dashboard',
        icon: <DashboardIcon />,
        key: '100',
        route: '/dashboard',
      },
      {
        name: 'My Spaces',
        icon: <AppsIcon />,
        key: '200',
        route: '/spaces',
        ...spacesMenuFilterProps,
        children: spacesIsLoading ? [{ key: 'spaces-loader', component: <MenuSkeleton /> }] : spacesMenu,
      },
      {
        name: 'Contacts',
        icon: <ImportContactsIcon />,
        key: '300',
        route: '/contacts',
        isHidden: !authUser || authUser.denyContactList,
      },
      {
        name: 'Offline',
        icon: <WifiOffIcon />,
        key: 'offline',
        route: '/offline',
      },
    ];

    if (isAdmin || authorities.includes('MAILING_LIST_ADMIN')) {
      items.push({
        name: 'Admin',
        icon: <SettingsIcon />,
        key: '400',
        route: '/admin',
        isHidden: !isAdmin && !authorities.includes('MAILING_LIST_ADMIN'),
        children: [
          {
            name: 'Users',
            icon: <PersonIcon />,
            key: 6,
            route: '/admin/users',
            isHidden: !isAdmin,
          },
          {
            name: 'Roles',
            icon: <VerifiedUserIcon />,
            key: 'roles',
            route: '/admin/roles',
            isHidden: !isAdmin,
          },
          {
            name: 'Space permissions',
            icon: <LockIcon />,
            key: 7,
            route: '/admin/permissions',
            hasFilter: true,
            filterLabel: 'Filter by role...',
            children: roles
              .filter(role => !isAdminRole(role))
              .map(role => ({
                name: role.name,
                key: `permissions-role-${role.id}`,
                route: `/admin/permissions/${role.id}`,
              })),
            isHidden: !isAdmin,
          },
          {
            name: 'Global permissions',
            icon: <LockIcon />,
            key: 'global-permissions',
            route: '/admin/global-permissions',
            isHidden: !isAdmin,
          },
          {
            name: 'Default dashboards',
            icon: <DashboardIcon />,
            key: 'default-dashboards',
            route: '/admin/default-dashboards',
            isHidden: !isAdmin,
          },
          {
            name: 'Spaces',
            icon: <AppsIcon />,
            key: 8,
            route: '/admin/spaces',
            isHidden: !isAdmin,
          },
          {
            name: 'Tags',
            icon: <LabelIcon />,
            key: 9,
            route: '/admin/tags',
            isHidden: !isAdmin,
          },
          {
            name: 'Mailing Lists',
            icon: <EmailIcon />,
            key: 'mailing-lists',
            route: '/admin/mailing-lists',
            isHidden: !isAdmin && !authorities.includes('MAILING_LIST_ADMIN'),
          },
        ],
      });
    }

    return (
      <div>
        <Menu items={items} />
      </div>
    );
  }
}
AppSidebar.propTypes = {
  spaces: PropTypes.arrayOf(PropTypes.object).isRequired,
  roles: PropTypes.arrayOf(PropTypes.any), // eslint-disable-line
  authUser: PropTypes.objectOf(PropTypes.any), // eslint-disable-line
  spacesIsLoading: PropTypes.bool.isRequired,
  fetchAll: PropTypes.func.isRequired,
  fetchMyUser: PropTypes.func.isRequired,
  fetchAllRoles: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  activeSpaceRoles: PropTypes.arrayOf(PropTypes.any).isRequired,
  roleIdsPendingAudit: PropTypes.arrayOf(PropTypes.object).isRequired,
  authorities: PropTypes.arrayOf(PropTypes.any).isRequired,
  loginSuccess: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  spaces: getAllSpaces(state),
  spacesIsLoading: getSpacesIsLoading(state),
  roles: getAllRoles(state),
  authUser: getAuthUser(state),
  authorities: getAuthUserAuthorities(state),
  isAdmin: getIsAdmin(state),
  activeSpaceRoles: getActiveSpaceRoles(state),
  roleIdsPendingAudit: getActiveStateAuditPendingRoleIds(state),
});

const mapDispatchToProps = {
  fetchAll: fetchAllSpaces,
  fetchMyUser,
  fetchAllRoles,
  loginSuccess,
};

export default connect(mapStateToProps, mapDispatchToProps)(AppSidebar);
